import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, User, ShieldCheck, Eye, EyeOff } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1512403754473-27835f7b9984?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [idCode, setIdCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(idCode, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left hero */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden" style={{ background: "#111318" }}>
        <img src={HERO} alt="finance" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(17,19,24,0.85), rgba(17,19,24,0.55))" }} />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center font-serif font-bold text-xl text-[#111318]">S</div>
            <div>
              <div className="font-serif text-2xl tracking-wide">SAVIOR</div>
              <div className="text-xs text-white/60 tracking-widest">PT. SAVIOR PRIME INDONESIA</div>
            </div>
          </div>
          <div>
            <h1 className="font-serif text-5xl leading-tight mb-4">
              Precision in <span className="gold-text">Finance</span>,<br />Excellence in Service.
            </h1>
            <p className="text-white/60 max-w-md text-sm leading-relaxed">
              Integrated work system for business finance consultants — clients, tax, attendance & payroll under one elegant roof.
            </p>
          </div>
          <div className="text-white/40 text-xs">© {new Date().getFullYear()} PT. Savior Prime Indonesia</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[var(--bg-main)]">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl gold-gradient flex items-center justify-center font-serif font-bold text-xl text-[#111318]">S</div>
            <div className="font-serif text-2xl">SAVIOR</div>
          </div>
          <h2 className="font-serif text-3xl mb-1">Welcome back</h2>
          <p className="text-[var(--text-muted)] text-sm mb-8">Sign in with your ID Code to continue.</p>

          <form onSubmit={(e) => submit(e)} className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">ID Code</label>
              <div className="mt-2 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <input
                  data-testid="login-id-code-input"
                  value={idCode}
                  onChange={(e) => setIdCode(e.target.value.toUpperCase())}
                  placeholder="e.g. SPV001"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[var(--border-subtle)] bg-white font-mono focus:border-[var(--gold)] focus:ring-2 focus:ring-[var(--gold)]/20 outline-none transition"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Password</label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-11 py-3 rounded-xl border border-[var(--border-subtle)] bg-white focus:border-[var(--gold)] focus:ring-2 focus:ring-[var(--gold)]/20 outline-none transition"
                />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--gold)]">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-[var(--urgent)] bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit-button" disabled={busy} className="btn-gold w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              <ShieldCheck className="w-4 h-4" /> {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
